import type { CardProps } from '../types'

import {
  CaretDownOutlined,
  CaretUpOutlined,
  CheckOutlined,
  CloseOutlined,
  MinusOutlined
} from '@ant-design/icons'

import { Tooltip } from 'antd'
import cx from 'classnames'
import { Fragment } from 'react'

import { formatValue } from '../functions'

export function Card({ title, hasData, value, error, details }: CardProps): JSX.Element {
  return (
    <div className='pr-4 bg-white rounded-xl border border-solid border-stone-200'>
      <div className='flex flex-row justify-between items-start px-3 pt-4'>
        <div className='flex flex-row gap-3 items-center'>
          <div
            className={cx(
              'w-6 h-6 rounded-full flex items-center justify-center text-[12px]',
              !hasData
                ? 'bg-gray-200 text-gray-400'
                : error?.showError
                  ? 'bg-[#FCCCC3] text-red-600'
                  : 'bg-[#C3FCC7] text-green-600'
            )}
          >
            {!hasData ? (
              <MinusOutlined />
            ) : error?.showError ? (
              <CloseOutlined />
            ) : (
              <CheckOutlined />
            )}
          </div>

          <span className='text-base font-semibold'>{title}</span>
        </div>
        <div className='relative flex flex-col justify-center items-end'>
          <span className='text-base font-semibold'>
            {!hasData ? null : typeof value === 'number' ? formatValue(value) : null}
          </span>

          {hasData && typeof error?.value === 'number' && (
            <div className='text-red-600 flex flex-row items-center gap-1'>
              {error?.showError && typeof error.value === 'number' && (
                <div className='text-red-600 flex flex-row items-center gap-1'>
                  {error.value > 0 ? <CaretDownOutlined /> : <CaretUpOutlined />}
                  <span>{formatValue(Math.abs(error.value))}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {details?.length && (
        <Fragment>
          <div className='flex flex-col gap-4 py-4 pr-3 pl-12'>
            {details.map(d => {
              return d.title ? (
                <div key={`${d.value}-${d.title}`}>
                  <div className='flex flex-row justify-between items-center'>
                    <span className='text-sm font-semibold'>{d.title}</span>
                    <span className='text-base'>
                      {typeof d.value === 'number' ? formatValue(d.value) : '–'}
                    </span>
                  </div>
                  <div className='flex flex-col gap-2 pl-2 mt-2'>
                    {d.data.map(data => (
                      <Tooltip
                        key={`${data.value}-${data.title}`}
                        title={!!data.value || data.forceNumber ? data.tooltip : undefined}
                        showArrow={false}
                      >
                        <div className='flex flex-row justify-between items-center'>
                          <span className='text-xs font-semibold text-zinc-600'>{data.title}</span>
                          <span className='text-base'>
                            {!!data.value || data.forceNumber ? formatValue(data.value) : '–'}
                          </span>
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              ) : (
                d.data.map((data, idx) => (
                  <div
                    key={`${d.value}-${d.title}-${idx}`}
                    className='flex flex-row justify-between items-center'
                  >
                    <span className='font-semibold text-zinc-600'>{data.title}</span>
                    <span className='text-base'>
                      {typeof data.value === 'number' ? formatValue(data.value) : '–'}
                    </span>
                  </div>
                ))
              )
            })}
          </div>
        </Fragment>
      )}
    </div>
  )
}
